<template>
  <span>
    <main-large-dialog
      v-bind="$props"
      :is-visible.sync="computedIsVisible"
      :is-valid="!isDialogDisabled"
      :title="$i18n.t('dialog.title.loadData')"
      dialog-width="60%"
      min-body-height="400px"
      @agree="agreeHandler"
      max-height="80vh"
      no-paddings-y
      no-paddings-x
      :agree-text="computedAgreeText"
      disagree-text="dialog.disagreeClose"
    >
      <template #body>
        <v-tabs v-model="currentTab" ref="tabs" class="fill-height d-flex flex-column" hide-slider color="font">
          <v-tab
            v-for="tab in tabs"
            :key="tab.value"
            v-text="tab.text"
            active-class="activeTab"
            class="font--text"
            style="text-transform: none;"
            :disabled="isTabsDisabled"
          >
          </v-tab>
          <v-tabs-items v-model="currentTab" class="fill-height">
            <v-tab-item style="height: inherit">
              <template v-if="isFetched">
                <v-radio-group v-model="selectedScenarioId" v-if="computedModels.length > 0">
                  <v-treeview
                    :open="openedNodes"
                    class="text-body-2"
                    :items="computedModels"
                    item-children="scenarios"
                    item-key="key"
                    style="text-align: left;"
                  >
                    <template #label="{ item }">
                      <v-row no-gutters align="center">
                        <v-col cols="auto">
                          {{ item.name }}
                        </v-col>
                        <v-col
                          cols="auto"
                          class="font-italic font-weight-light pl-1 pb-1"
                          style="font-size: 12px"
                          v-if="item.quality"
                        >
                          {{ `(${item.quality.split('@')[0]})` }}
                        </v-col>
                      </v-row>
                    </template>
                    <template #prepend="{ item }">
                      <v-icon v-if="item.added_at" size="19">
                        mdi-hexagon-multiple
                      </v-icon>
                      <v-radio :value="item.id" v-else style="font-size: 19px;"> </v-radio>
                    </template>
                    <template #append="{ item }">
                      <span v-if="item.added_at" class="pr-5">
                        <dots-menu :items="dotsMenuItems" @delete="openDeletingDialog(item.id, item.name)"> </dots-menu>
                      </span>
                    </template>
                  </v-treeview>
                </v-radio-group>
                <v-row no-gutters v-else class="justify-center align-center fill-height">
                  <v-col cols="auto">
                    <v-subheader class="text-center">
                      {{ $i18n.t('admin.noData') }}
                    </v-subheader>
                  </v-col>
                </v-row>
              </template>
              <main-loader v-else> </main-loader>
            </v-tab-item>
            <v-tab-item style="height: inherit">
              <v-row no-gutters>
                <v-col cols="10" class="px-5 py-6">
                  <main-input-dialog-row v-for="input in fileInput" v-bind="input" :key="input.name">
                    <template #name.input="{ dataType }">
                      <v-col cols="4">
                        <data-input :dataType="dataType" v-model="name"></data-input>
                      </v-col>
                    </template>
                    <template #srid.input="{ dataType }">
                      <v-col cols="4">
                        <data-input :dataType="dataType" v-model="srid"></data-input>
                      </v-col>
                    </template>
                    <template #source.input="data">
                      <v-col cols="4">
                        <data-input v-bind="data" v-model="source"></data-input>
                      </v-col>
                    </template>

                    <template #file.input>
                      <v-row no-gutters>
                        <v-col>
                          <file-input
                            @successfullyAdded="onSuccessfullyAdded"
                            ref="file"
                            @error="onImportError"
                            @loading="isUploading = true"
                            :expanded-files-extensions="filesExtensions"
                            :options="dropzoneOptions"
                            :events="{
                              'vdropzone-sending': sendingEvent,
                            }"
                          ></file-input>
                        </v-col>
                      </v-row>
                    </template>
                  </main-input-dialog-row>
                </v-col>
              </v-row>
            </v-tab-item>
          </v-tabs-items>
        </v-tabs>
      </template>
    </main-large-dialog>
    <main-dialog
      v-if="isDeleteDialogLoaded"
      :is-visible.sync="isDeleteDialogVisible"
      :title="$i18n.t('dialog.title.deleteModel')"
      :body="$i18n.t('dialog.body.deleteModel', { name: deletedModelName })"
      @agree="handleModelDelete"
      :loading="isConfirmDeleteButtonLoading"
      agree-text="dialog.agreeDelete"
    ></main-dialog>
  </span>
</template>

<script>
import { call, get } from 'vuex-pathify';

import MainInputDialogRow from '@/components/MainInputDialogRow';
import FileInput from '@/components/FileInput';
import MainLoader from '@/components/MainLoader';
import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
import MainLargeDialog from '@/components/MainLargeDialog';

export default {
  name: 'LoadModelingDataDialog',
  components: {
    MainLargeDialog,
    MainInputDialogRow,
    FileInput,
    MainLoader,
    DataInput,
    DotsMenu,
    MainDialog: () => import('@/components/MainDialog'),
  },
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    netType: {
      type: String,
      required: true,
    },
  },
  data: vm => ({
    isDeleteDialogVisible: false,
    isDeleteDialogLoaded: false,
    isConfirmDeleteButtonLoading: false,
    deletedModelName: undefined,

    isConfirmButtonLoading: false,
    isDialogVisible: false,
    isDialogLoaded: false,
    fileAdded: false,
    isFetched: true,
    isValid: false,
    openedNodes: [],
    isUploading: false,

    name: undefined,
    srid: undefined,
    source: undefined,
    currentTab: 0,
    tabs: [
      {
        text: vm.$i18n.t('dialog.body.data'),
        value: 'web',
      },
      {
        text: vm.$i18n.t('dialog.body.import'),
        value: 'qgis',
      },
    ],
    selectedScenarioId: undefined,
    dotsMenuItems: [
      {
        name: 'delete',
      },
    ],
  }),
  computed: {
    dropzoneOptions() {
      return {
        url: `${process.env.VUE_APP_API_URL}/${this.netType}/modeling_results/import`,
        method: 'post',
        autoProcessQueue: false,
        acceptedFiles: '.zip',
        uploadMultiple: false,
        maxFiles: 1,
        paramName: 'model',
      };
    },
    filesExtensions() {
      return [
        {
          name: 'zip',
          url: `${process.env.VUE_APP_API_URL}/${this.netType}/modeling_results/import`,
          exts: ['zip'],
          canExtDuplicate: false,
          minFiles: 1,
          maxFiles: 1,
        },
      ];
    },
    isScenarioSelected() {
      return Boolean(this.selectedScenarioId);
    },
    isDialogDisabled() {
      return !this.isScenarioSelected && (this.currentTab !== 1 || this.isUploading);
    },
    isTabsDisabled() {
      return !this.isFetched || this.isUploading;
    },
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    models: get('modeling/models'),
    modelsArray() {
      return this.$_objectToArray(this.models[this.netType] || []);
    },
    computedModels() {
      return this.modelsArray.map(model => {
        return {
          ...model,
          key: `models_${model.id}`,
          scenarios: model.scenarios.map(scenario => {
            return { ...scenario, key: `scenarios_${scenario.id}` };
          }),
        };
      });
    },
    fileInput() {
      return [
        {
          name: 'name',
          translationPath: 'dialog.body.modelName',
          dataType: {
            name: 'text',
          },
        },
        {
          name: 'srid',
          translationPath: 'dialog.body.coordinateSystem',
          subtitle: 'dialog.body.typeSrid',
          dataType: {
            name: 'integer',
          },
        },
        ...(this.netType === 'water'
          ? [
              {
                cols: 'auto',
                name: 'source',
                translationPath: 'dialog.body.fileSource',
                subtitle: 'dialog.body.fileSourceSubtitle',
                dataType: {
                  name: 'select',
                },
                items: [
                  {
                    value: 'watergems',
                    text: 'OpenFlows WaterGEM',
                  },
                  {
                    value: 'epanet',
                    text: 'EPANET',
                  },
                ],
              },
            ]
          : []),
        {
          align: 'baseline',
          name: 'file',
          subtitle: this.$i18n.t('dialog.body.chooseModelingFile', {
            name: this.netType === 'water' ? 'WaterGEMS' : 'SewerGEMS',
          }),
          translationPath: 'dialog.body.file',
          rules: [v => !!v || 'rules.required'],
          isCustom: true,
        },
      ];
    },
    computedAgreeText() {
      return this.currentTab === 0 ? 'dialog.agreeLoad' : 'dialog.body.send';
    },
  },
  watch: {
    isVisible(nV) {
      if (!nV) {
        this.resetDialog();
      }
    },
  },
  methods: {
    getModels: call('modeling/getModels'),
    deleteModel: call('modeling/deleteModel'),
    openDeletingDialog(id, name) {
      this.deletedModelId = id;
      this.deletedModelName = name;
      this.isDeleteDialogVisible = true;
      this.isDeleteDialogLoaded = true;
    },
    async handleModelDelete() {
      try {
        const { netType, deletedModelId: id } = this;
        this.isConfirmDeleteButtonLoading = true;
        await this.deleteModel({ netType, id });
        await this.getModels(netType);
        this.isConfirmDeleteButtonLoading = false;
        this.isDeleteDialogVisible = false;
      } catch (error) {
        this.isConfirmDeleteButtonLoading = false;
        console.log('  error', error);
      }
    },
    agreeHandler() {
      if (this.currentTab === 0) {
        this.addScenario();
      } else {
        this.upload();
      }
    },
    upload() {
      this.$refs.file[0].$refs.dropzone.processQueue();
    },
    onImportError() {
      this.isUploading = false;
    },
    sendingEvent(files, xhr, formData) {
      formData.append('name', this.name);
      formData.append('srid', this.srid);
      if (this.netType === 'water') {
        formData.append('source', this.source);
      }
    },
    resetDialog() {
      this.currentTab = 0;
      this.isUploading = false;
      this.selectedScenarioId = null;
      this.srid = undefined;
      this.name = undefined;
      this.source = undefined;
      if (this.$refs.file) {
        this.$refs.file[0].$refs.dropzone.removeAllFiles();
      }
    },
    async refetchModels() {
      try {
        this.isFetched = false;
        await this.getModels(this.netType);
        setTimeout(() => {
          this.isFetched = true;
        }, 500);
      } catch (error) {
        this.isFetched = true;
        console.log(this.error);
      }
    },
    addScenario() {
      this.$router.push({
        name: 'modelingResultsScenario',
        params: {
          scenarioId: this.selectedScenarioId,
        },
      });
      localStorage.setItem(`scenarioId-${this.netType}`, JSON.stringify(this.selectedScenarioId));
      this.computedIsVisible = false;
    },
    scrollToLastNode() {
      setTimeout(() => {
        const htmlNodes = document.getElementsByClassName('v-treeview-node');
        htmlNodes[htmlNodes.length - 1].scrollIntoView({ behavior: 'smooth' });
      }, 1000);
    },
    async onSuccessfullyAdded() {
      await this.refetchModels();
      this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
        message: this.$i18n.t('success.importedModel'),
      });
      this.openedNodes = [`models_${this.modelsArray[this.modelsArray.length - 1].id}`];
      this.currentTab = 0;
      this.isUploading = false;
      this.scrollToLastNode();
    },
  },
};
</script>

<style lang="scss" scoped>
.activeTab {
  background-color: rgba(207, 207, 207, 0.25);
}
::v-deep {
  .v-input--selection-controls__input .v-icon {
    font-size: 19px;
  }
  .v-slide-group {
    z-index: 99;
    position: sticky;
    top: 0;
    box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12) !important;
  }
}
</style>
